import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  fetchRooms, createRoom, updateRoom, clearRoomMessages, deleteRoom,
  fetchUsers, fetchRoomMembers, addRoomMember, removeRoomMember
} from '../services/api';

const s = {
  page: { minHeight: '100vh', background: '#080b10' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px',
    borderBottom: '1px solid #1e2a36',
    background: '#0d1117',
    position: 'sticky', top: 0, zIndex: 100,
  },
  navBrand: {
    display: 'flex', alignItems: 'center', gap: '12px',
    fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '800',
    color: '#e2eaf3',
  },
  navBadge: {
    fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
    background: 'rgba(0,229,255,0.1)', color: '#00e5ff',
    border: '1px solid rgba(0,229,255,0.2)',
    borderRadius: '4px', padding: '2px 8px', letterSpacing: '0.1em',
  },
  navLinks: { display: 'flex', gap: '16px', alignItems: 'center' },
  navLink: {
    fontSize: '12px', color: '#7a90a4', textDecoration: 'none',
    padding: '6px 12px', borderRadius: '6px',
    border: '1px solid transparent',
  },
  navLinkActive: {
    color: '#00e5ff', borderColor: 'rgba(0,229,255,0.2)',
    background: 'rgba(0,229,255,0.05)',
  },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 32px' },
  sectionTitle: {
    fontFamily: 'Syne, sans-serif', fontSize: '22px', fontWeight: '700',
    color: '#e2eaf3', marginBottom: '24px',
  },
  createCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '28px', marginBottom: '40px',
  },
  createGrid: { display: 'grid', gridTemplateColumns: '1fr 140px 160px auto', gap: '12px', alignItems: 'end' },
  fieldLabel: {
    fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase',
    color: '#7a90a4', marginBottom: '8px', display: 'block',
  },
  pinInputWrap: { position: 'relative' },
  pinHint: { fontSize: '11px', color: '#3d5166', marginTop: '6px' },
  roomsGrid: { display: 'grid', gap: '16px' },
  roomCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '12px', padding: '20px 24px',
    display: 'grid', gridTemplateColumns: '1fr auto',
    alignItems: 'center', gap: '16px',
    transition: 'border-color 0.2s',
  },
  roomName: { fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: '700', color: '#e2eaf3', marginBottom: '6px' },
  roomMeta: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' },
  metaChip: {
    fontSize: '11px', fontFamily: 'JetBrains Mono, monospace',
    color: '#7a90a4', display: 'flex', alignItems: 'center', gap: '5px',
  },
  pinChip: {
    fontSize: '12px', fontFamily: 'JetBrains Mono, monospace',
    background: 'rgba(0,229,255,0.08)', color: '#00e5ff',
    border: '1px solid rgba(0,229,255,0.15)',
    borderRadius: '6px', padding: '2px 10px',
    letterSpacing: '0.15em',
  },
  ownerChip: {
    fontSize: '12px', fontFamily: 'JetBrains Mono, monospace',
    background: 'rgba(255,183,0,0.08)', color: '#ffb300',
    border: '1px solid rgba(255,183,0,0.15)',
    borderRadius: '6px', padding: '2px 10px',
  },
  urlRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
    marginTop: '10px', padding: '8px 12px',
    background: '#131920', borderRadius: '8px',
    border: '1px solid #1e2a36',
  },
  urlText: { fontSize: '12px', color: '#3d5166', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  actionRow: { display: 'flex', gap: '8px' },
  emptyState: {
    textAlign: 'center', padding: '60px 24px',
    color: '#3d5166', fontSize: '13px',
  },
  toast: {
    position: 'fixed', bottom: '24px', right: '24px',
    background: '#131920', border: '1px solid #00e676',
    borderRadius: '10px', padding: '12px 20px',
    color: '#00e676', fontSize: '13px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    zIndex: 1000, animation: 'fadeUp 0.3s ease',
  },
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200, backdropFilter: 'blur(4px)',
  },
  modalCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%',
  },
  modalTitle: { fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '700', marginBottom: '12px' },
  modalSub: { color: '#7a90a4', fontSize: '13px', lineHeight: '1.6', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  filterBtns: { display: 'flex', gap: '12px', marginBottom: '24px' },
  filterBtn: {
    flex: 1, padding: '12px', borderRadius: '8px',
    border: '1px solid #1e2a36', background: '#131920',
    color: '#7a90a4', fontSize: '13px', cursor: 'pointer',
  },
  filterBtnActive: {
    borderColor: '#00e5ff', color: '#00e5ff',
    background: 'rgba(0,229,255,0.05)',
  },
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return <div style={s.toast}>✓ {message}</div>;
}

function ClearModal({ message, onConfirm, onCancel }) {
  const [filter, setFilter] = useState('all');

  return (
    <div style={s.modal} onClick={onCancel}>
      <div style={s.modalCard} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>Clear Messages</div>
        <div style={s.modalSub}>{message}</div>
        <div style={s.filterBtns}>
          <button
            style={filter === 'all' ? { ...s.filterBtn, ...s.filterBtnActive } : s.filterBtn}
            onClick={() => setFilter('all')}
          >
            Clear All<br /><small style={{ fontSize: '10px' }}>Read + Unread</small>
          </button>
          <button
            style={filter === 'read' ? { ...s.filterBtn, ...s.filterBtnActive } : s.filterBtn}
            onClick={() => setFilter('read')}
          >
            Clear Read Only<br /><small style={{ fontSize: '10px' }}>Keep Unread</small>
          </button>
        </div>
        <div style={s.modalBtns}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(filter)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={s.modal} onClick={onCancel}>
      <div style={s.modalCard} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>Confirm Action</div>
        <div style={s.modalSub}>{message}</div>
        <div style={s.modalBtns}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// Edit Room modal — edit name, password, owner, and manage members.
function EditRoomModal({ room, users, onSave, onAddMember, onRemoveMember, onClose }) {
  const [name, setName] = useState(room.name);
  const [password, setPassword] = useState(room.password || '');
  const [ownerId, setOwnerId] = useState(room.ownerId || '');
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [saving, setSaving] = useState(false);

  // Load members when the modal opens
  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const { data } = await fetchRoomMembers(room.id);
      setMembers(data);
    } catch (e) {
      // silent
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(room.id, { name, password, ownerId });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    try {
      await onAddMember(room.id, selectedUserId);
      setSelectedUserId('');
      await loadMembers();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await onRemoveMember(room.id, userId);
      await loadMembers();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove member');
    }
  };

  const memberIds = new Set(members.map(m => m.userId));
  const availableUsers = users.filter(u => !memberIds.has(u.id) && u.role !== 'ADMIN');

  return (
    <div style={s.modal} onClick={onClose}>
      <div style={{ ...s.modalCard, maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>Edit Room</div>

        {/* Room settings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={s.fieldLabel}>Room Name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label style={s.fieldLabel}>PIN</label>
            <input
              className="input"
              value={password}
              onChange={e => setPassword(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              style={{ letterSpacing: '0.2em', textAlign: 'center' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={s.fieldLabel}>Owner</label>
          <select className="input" value={ownerId} onChange={e => setOwnerId(e.target.value)}>
            <option value="">— None —</option>
            {users.filter(u => u.role !== 'ADMIN').map(u => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
        </div>

        {/* Members section */}
        <div style={{ borderTop: '1px solid #1e2a36', paddingTop: '20px', marginBottom: '20px' }}>
          <label style={s.fieldLabel}>Room Members</label>

          {loadingMembers ? (
            <div style={{ color: '#3d5166', fontSize: '13px', padding: '12px 0' }}>Loading members...</div>
          ) : members.length === 0 ? (
            <div style={{ color: '#3d5166', fontSize: '13px', padding: '12px 0' }}>No members assigned yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {members.map(m => (
                <div key={m.memberId} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: '#131920',
                  border: '1px solid #1e2a36', borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#e2eaf3' }}>{m.username}</span>
                    {m.isOwner && (
                      <span style={{
                        fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
                        background: 'rgba(255,183,0,0.08)', color: '#ffb300',
                        border: '1px solid rgba(255,183,0,0.15)',
                        borderRadius: '4px', padding: '2px 6px',
                      }}>OWNER</span>
                    )}
                    <span style={{ fontSize: '11px', color: '#7a90a4' }}>{m.role}</span>
                  </div>
                  {!m.isOwner && (
                    <button
                      className="btn btn-danger"
                      onClick={() => handleRemoveMember(m.userId)}
                      style={{ fontSize: '10px', padding: '4px 10px' }}
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add member dropdown */}
          {availableUsers.length > 0 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                className="input"
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Select a user to add...</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={handleAddMember}
                disabled={!selectedUserId}
                style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
              >
                + Add
              </button>
            </div>
          )}
        </div>

        {/* Save / Cancel */}
        <div style={s.modalBtns}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !name}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PinInput({ value, onChange }) {
  return (
    <div>
      <input
        className="input"
        type="text"
        inputMode="numeric"
        maxLength={4}
        value={value}
        onChange={e => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 4);
          onChange(v);
        }}
        placeholder="0000"
        style={{ letterSpacing: '0.3em', fontSize: '18px', textAlign: 'center' }}
      />
      <p style={s.pinHint}>4-digit PIN only</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newPin, setNewPin] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState('');
  const [confirm, setConfirm] = useState(null); // { type, message, action }
  const [clearModal, setClearModal] = useState(null); // { message, action }
  const [editRoom, setEditRoom] = useState(null); // room object being edited

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await fetchRooms();
      setRooms(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await fetchUsers();
      setUsers(data);
    } catch (e) {
      // silent fail — users are optional for room creation
    }
  }, []);

  useEffect(() => { loadRooms(); loadUsers(); }, [loadRooms, loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newPin.length !== 4) return;
    setCreating(true);
    try {
      await createRoom(newName, newPin, selectedOwnerId || undefined);
      setNewName('');
      setNewPin('');
      setSelectedOwnerId('');
      await loadRooms();
      await loadUsers();
      setToast('Room created successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const handleClear = (room) => {
    setClearModal({
      message: `Clear messages from "${room.name}"?`,
      action: async (filter) => {
        await clearRoomMessages(room.id, filter);
        await loadRooms();
        setToast(filter === 'read' ? 'Read messages cleared' : 'All messages cleared');
      }
    });
  };

  const handleDelete = (room) => {
    setConfirm({
      message: `Delete room "${room.name}" and all its messages permanently?`,
      action: async () => {
        await deleteRoom(room.id);
        await loadRooms();
        setToast('Room deleted');
      }
    });
  };

  const handleEdit = (room) => {
    setEditRoom(room);
  };

  const handleUpdateRoom = async (roomId, { name, password, ownerId }) => {
    try {
      await updateRoom(roomId, { name, password, ownerId });
      await loadRooms();
      await loadUsers();
      setEditRoom(null);
      setToast('Room updated successfully');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update room');
    }
  };

  const handleAddMember = async (roomId, userId) => {
    await addRoomMember(roomId, userId);
  };

  const handleRemoveMember = async (roomId, userId) => {
    await removeRoomMember(roomId, userId);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setToast('Link copied to clipboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navBrand}>
          <span>🔐</span>
          <span>SecretChat</span>
          <span style={s.navBadge}>ADMIN</span>
        </div>
        <div style={s.navLinks}>
          <Link to="/admin/dashboard" style={{ ...s.navLink, ...s.navLinkActive }}>Rooms</Link>
          <Link to="/admin/users" style={{ ...s.navLink }}>Users</Link>
          <button className="btn btn-ghost" onClick={handleLogout} style={{ fontSize: '12px' }}>
            Sign Out
          </button>
        </div>
      </nav>

      <div style={s.content}>
        {/* Create Room */}
        <h2 style={s.sectionTitle}>Create New Room</h2>
        <div style={s.createCard}>
          <form onSubmit={handleCreate}>
            <div style={s.createGrid}>
              <div>
                <label style={s.fieldLabel}>Room Name</label>
                <input
                  className="input"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Project Alpha"
                  required
                />
              </div>
              <div>
                <label style={s.fieldLabel}>4-Digit PIN</label>
                <PinInput value={newPin} onChange={setNewPin} />
              </div>
              <div>
                <label style={s.fieldLabel}>Owner (optional)</label>
                <select
                  className="input"
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {users
                    .filter(u => u.role === 'USER' || u.role === 'ROOM_OWNER')
                    .map(u => (
                      <option key={u.id} value={u.id}>{u.username}</option>
                    ))
                  }
                </select>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating || newPin.length !== 4 || !newName}
                style={{ marginBottom: '22px', whiteSpace: 'nowrap' }}
              >
                {creating ? '...' : '+ Create'}
              </button>
            </div>
          </form>
        </div>

        {/* Rooms List */}
        <h2 style={s.sectionTitle}>
          Active Rooms
          <span style={{ ...s.navBadge, marginLeft: '12px', verticalAlign: 'middle' }}>
            {rooms.length}
          </span>
        </h2>

        {loading ? (
          <div style={s.emptyState}>Loading rooms...</div>
        ) : rooms.length === 0 ? (
          <div style={s.emptyState}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
            No rooms yet. Create one above.
          </div>
        ) : (
          <div style={s.roomsGrid}>
            {rooms.map(room => (
              <div key={room.id} style={s.roomCard} className="fade-up">
                <div>
                  <div style={s.roomName}>{room.name}</div>
                  <div style={s.roomMeta}>
                    <span style={s.metaChip}>
                      <span className="pulse-dot" style={{ background: '#00e676' }} />
                      {room.messageCount} messages
                    </span>
                    <span style={s.metaChip}>
                      📅 {new Date(room.createdAt).toLocaleDateString()}
                    </span>
                    <span style={s.pinChip}>PIN: {room.password}</span>
                    {room.ownerName && (
                      <span style={s.ownerChip}>👤 {room.ownerName}</span>
                    )}
                  </div>
                  <div style={s.urlRow}>
                    <span style={s.urlText}>{room.chatUrl}</span>
                    <button
                      className="btn btn-ghost"
                      onClick={() => copyUrl(room.chatUrl)}
                      style={{ padding: '4px 12px', fontSize: '11px' }}
                    >
                      Copy
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => window.open(room.chatUrl, '_blank')}
                      style={{ padding: '4px 12px', fontSize: '11px' }}
                    >
                      Open ↗
                    </button>
                  </div>
                </div>
                <div style={s.actionRow}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => handleEdit(room)}
                    style={{ fontSize: '11px', color: '#00e5ff', borderColor: '#00e5ff' }}
                    title="Edit room and members"
                  >
                    ✎ Edit
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => handleClear(room)}
                    style={{ fontSize: '11px', color: '#ffb300', borderColor: '#ffb300' }}
                    title="Clear messages"
                  >
                    🗑 Clear
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(room)}
                    style={{ fontSize: '11px' }}
                    title="Delete room"
                  >
                    ✕ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && <Toast message={toast} onDone={() => setToast('')} />}

      {/* Clear Modal with filter */}
      {clearModal && (
        <ClearModal
          message={clearModal.message}
          onConfirm={async (filter) => {
            await clearModal.action(filter);
            setClearModal(null);
          }}
          onCancel={() => setClearModal(null)}
        />
      )}

      {/* Confirm Modal for delete */}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={async () => {
            await confirm.action();
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Edit Room Modal */}
      {editRoom && (
        <EditRoomModal
          room={editRoom}
          users={users}
          onSave={handleUpdateRoom}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          onClose={() => setEditRoom(null)}
        />
      )}
    </div>
  );
}
